import { SsuLogoPlaceholder, type WalletLedger, type WalletWithTotalAmountDTO } from '@capsicum/common';
import {
	hasEnoughToken,
	useMainCurrenciesQuery,
	usePartners,
	useWalletFormatter,
	useWalletQuery
} from '@capsicum/socios-core';
import { useCallback } from 'react';

import { type WalletConvertedPrices, type WalletFanToken } from '../models';
import { hasEnoughAvailableToken } from '../models/util/walletHasEnoughToken';

/**
 *
 * @param currencyId
 * @returns ledger - raw data returned from BE
 * @returns walletConvertedPrices - formatted balance in default and alternative currency
 * @returns partnerLogo
 * @returns hasEnoughToken - function to compare provided amount with total token balance in user wallet
 */
export const useWalletFanToken = (currencyId?: string) => {
	const { formatBalance } = useWalletFormatter();
	const { getPartnerBy } = usePartners();

	const { data: currencies } = useMainCurrenciesQuery();
	const { altCurrency, defaultCurrency } = currencies ?? {};

	return useWalletQuery(
		useCallback(
			(data: undefined | WalletWithTotalAmountDTO): WalletFanToken => {
				const ledger = (data?.ledgers?.find(l => l?.currency?.id === currencyId) as WalletLedger) || [];
				const partner = getPartnerBy(p => p.currencyId === ledger.currency?.id);

				const walletConvertedPrices: WalletConvertedPrices = {
					altCurrency: formatBalance(altCurrency, ledger),
					defaultCurrency: formatBalance(defaultCurrency, ledger)
				};

				const totalBalance = Number(ledger?.balance || 0) + Number(ledger?.lockedBalance || 0);
				const availableTokenBalance = Number(ledger?.balance || 0);

				return {
					availableTokenBalance: availableTokenBalance.toString(),
					hasEnoughAvailableToken: hasEnoughAvailableToken(availableTokenBalance),
					hasEnoughToken: hasEnoughToken(totalBalance),
					ledger,
					partner: {
						blurHash: partner?.images?.partnerLogo?.blurHash,
						id: partner?.id,
						logo: partner?.images?.partnerLogo?.url || (SsuLogoPlaceholder as unknown as string),
						name: partner?.name
					},
					totalBalance,
					walletConvertedPrices
				};
			},
			[currencyId, defaultCurrency, altCurrency, formatBalance, getPartnerBy]
		)
	);
};
